import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { formatDate } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { toast } from "sonner";
import StatusBadge from "../components/StatusBadge";
import { useAuth } from "../context/AuthContext";
import { Plus, Pencil, Trash2, ShieldCheck } from "lucide-react";

const empty = { name: "", email: "", status: "Active", password: "" };

export default function Admins() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = () => api.get("/users").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ name: u.name, email: u.email, status: u.status || "Active", password: "" }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    if (!editing && !form.password) return toast.error("Password is required for a new admin");
    const payload = { ...form };
    if (!payload.password) delete payload.password;
    try {
      if (editing) await api.put(`/users/${editing.id}`, payload);
      else await api.post("/users", payload);
      toast.success(editing ? "Admin updated" : "Admin added");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/users/${confirmDelete.id}`);
      toast.success("Admin deleted");
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admins-page">
      <div className="flex justify-between items-start flex-wrap gap-3">
        <div>
          <h1 className="heading text-2xl font-extrabold tracking-tight">Admins</h1>
          <p className="text-sm text-muted-foreground">Manage administrator accounts that can sign in to MealLedger. Each admin can create daily orders, record payments, and view reports.</p>
        </div>
        <Button onClick={openNew} data-testid="btn-add-admin" className="bg-emerald-600 hover:bg-emerald-700">
          <Plus size={16} className="mr-1" />Add Admin
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-xs uppercase text-muted-foreground">
              <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Role</th><th className="p-3">Status</th><th className="p-3">Added</th><th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-t table-row-hover" data-testid={`admin-row-${u.id}`}>
                <td className="p-3 font-medium">
                  {u.name}
                  {u.email === user?.email && <span className="ml-2 text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">You</span>}
                </td>
                <td className="p-3 mono text-muted-foreground">{u.email}</td>
                <td className="p-3"><span className="inline-flex items-center gap-1 text-xs text-slate-700"><ShieldCheck size={12} />Admin</span></td>
                <td className="p-3"><StatusBadge status={u.status || "Active"} /></td>
                <td className="p-3 text-muted-foreground text-xs">{u.created_at ? formatDate(u.created_at) : "—"}</td>
                <td className="p-3 text-right">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(u)} data-testid={`btn-edit-admin-${u.id}`}><Pencil size={14} className="mr-1" />Edit</Button>
                  {u.email !== user?.email && (
                    <Button variant="ghost" size="sm" className="text-rose-700 hover:text-rose-800 hover:bg-rose-50 ml-1" onClick={() => setConfirmDelete(u)} data-testid={`btn-delete-admin-${u.id}`}><Trash2 size={14} /></Button>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">No admins yet.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="admin-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Admin" : "Add Admin"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-name" className="mt-1.5" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="admin-email" className="mt-1.5" /></div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="admin-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Active">Active</SelectItem><SelectItem value="Inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label>{editing ? "New password (leave empty to keep current)" : "Password *"}</Label>
              <Input type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "Leave empty to keep current" : "Set a password"} className="mt-1.5 mono" data-testid="admin-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="btn-save-admin" className="bg-emerald-600 hover:bg-emerald-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => !v && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this admin?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="block font-medium text-slate-900 dark:text-slate-100 mb-2">{confirmDelete?.name} · {confirmDelete?.email}</span>
              This admin will no longer be able to sign in. Payments they previously received or recorded will keep their name for audit purposes.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} data-testid="btn-confirm-delete-admin" className="bg-rose-600 hover:bg-rose-700">Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
